/**
 * Admin · Limpeza da Base — Sprint Corretiva 2 (Item 7).
 *
 * Provides a SAFE 2-step flow:
 *   1) Preview: server scans collections for records classified as test/seed/demo.
 *   2) Commit: only after explicit user confirmation per category.
 */
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ShieldAlert, Trash2, Eye, CheckSquare, Square, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const CAT_LABELS = {
  companies: "Empresas",
  sectors: "Setores",
  job_roles: "Cargos",
  work_boards: "Quadros de trabalho",
  employees: "Colaboradores",
  candidates: "Candidatos",
  vacancies: "Vagas",
  cost_centers: "Centros de custo",
};

export default function AdminCleanupPage() {
  const { user, hasRole } = useAuth();
  const isMaster = user?.role === "master";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [selected, setSelected] = useState({});

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/cleanup/preview");
      setData(d);
      // Pre-select nothing, user must opt-in
      setSelected({});
      toast.success(`${d.grand_total} registro(s) candidato(s) à exclusão`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const toggleAll = (cat, checked) => setSelected((p) => ({ ...p, [cat]: checked }));

  const commit = async () => {
    const cats = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (cats.length === 0) { toast.error("Selecione ao menos uma categoria"); return; }
    if (!window.confirm(
      `⚠️ ATENÇÃO\n\nExcluir DEFINITIVAMENTE os registros das categorias: ${cats.join(", ")}.\n\n` +
      `Esta ação não pode ser desfeita. Confirma?`
    )) return;
    const confirm2 = window.prompt("Para confirmar, digite EXCLUIR-TUDO:");
    if (confirm2 !== "EXCLUIR-TUDO") { toast.info("Cancelado."); return; }
    setCommitting(true);
    try {
      const { data: r } = await api.post("/admin/cleanup/commit", { confirm: true, categories: cats });
      toast.success(`Limpeza concluída — ${r.grand_total} registro(s) removido(s)`);
      fetchPreview();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setCommitting(false); }
  };

  if (!isMaster && !hasRole("master")) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-6 text-center">
        <ShieldAlert className="h-8 w-8 mx-auto text-amber-600 mb-2" />
        <div className="font-display text-lg font-semibold text-amber-900">Acesso restrito</div>
        <p className="text-[13px] text-amber-800 mt-1">Esta tela é exclusiva para perfil master.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-cleanup-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-rose-700 font-medium">
            Administração · Risco alto
          </div>
          <h1 className="font-display text-3xl font-semibold flex items-center gap-2">
            <ShieldAlert className="h-7 w-7 text-rose-600" /> Limpeza da Base de Dados
          </h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground max-w-[760px]">
            Identifica registros marcados como <b>seed/demo/teste/mock</b> em cada coleção
            e permite removê-los <b>com confirmação dupla</b>. Empresas reais (NDB Holding, Sayonara, Usina ALCON)
            estão protegidas.
          </p>
        </div>
        <Button onClick={fetchPreview} disabled={loading} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="cleanup-preview-btn">
          {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
          {data ? "Atualizar prévia" : "Rodar prévia"}
        </Button>
      </div>

      {data && (
        <>
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-[12.5px] text-rose-900 flex items-start gap-2">
            <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <b>{data.grand_total}</b> registro(s) candidatos à exclusão. Empresas protegidas: {data.protected_companies.join(", ")}.
              Marque as caixas das categorias que deseja apagar e clique em <b>Apagar selecionados</b> abaixo.
            </div>
          </div>

          <div className="space-y-3">
            {Object.entries(data.details).map(([cat, rows]) => (
              <div key={cat} className="rounded-lg border border-border bg-white overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleAll(cat, !selected[cat])}
                      data-testid={`cleanup-toggle-${cat}`}
                      disabled={rows.length === 0}
                      className="text-slate-700"
                    >
                      {selected[cat] ? <CheckSquare className="h-5 w-5 text-rose-600" /> : <Square className="h-5 w-5" />}
                    </button>
                    <div>
                      <div className="font-display font-semibold">{CAT_LABELS[cat] || cat}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {rows.length} registro(s) classificados como teste/demo/seed
                      </div>
                    </div>
                  </div>
                  <Badge variant="outline" className={rows.length === 0 ? "" : "bg-rose-50 border-rose-300 text-rose-700"}>
                    {rows.length}
                  </Badge>
                </div>
                {rows.length > 0 && (
                  <div className="max-h-[180px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-[11px]">ID</TableHead>
                          <TableHead className="text-[11px]">Identificação</TableHead>
                          <TableHead className="text-[11px]">Origem / fonte</TableHead>
                          <TableHead className="text-[11px]">Critério</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.slice(0, 100).map((r) => (
                          <TableRow key={r.id}>
                            <TableCell className="font-mono text-[10.5px] text-muted-foreground">{(r.id || "").slice(0, 8)}…</TableCell>
                            <TableCell className="text-[12px]">{r.identifier || "—"}</TableCell>
                            <TableCell className="text-[11.5px]">{r.source || "—"}</TableCell>
                            <TableCell className="text-[11px] text-muted-foreground">{r.criterio}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between rounded-lg border border-rose-300 bg-rose-50 p-4">
            <div>
              <div className="font-display font-semibold text-rose-800">
                {Object.values(selected).filter(Boolean).length} categoria(s) selecionada(s)
              </div>
              <div className="text-[12px] text-rose-700">
                Esta operação NÃO pode ser desfeita. Necessário digitar "EXCLUIR-TUDO" para confirmar.
              </div>
            </div>
            <Button
              onClick={commit}
              variant="destructive"
              disabled={committing || Object.values(selected).filter(Boolean).length === 0}
              data-testid="cleanup-commit-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {committing ? "Apagando..." : "Apagar selecionados"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
